import { useCallback, useMemo, useState } from "react";
import { setDraftVoucher } from "../store/draftReservation";
import { setUserVirtualWalletBalance } from "../store/user";

import {
  formatPrice,
  useGetVirtualWalletBalanceLazyQuery,
  useStoreDiscountMutation,
} from "@stepapp/core";
import { StoredDiscountType } from "@stepapp/core/apiTypes";

import { useTranslation } from "react-i18next";
import { AppStorage } from "../services/AppStorage";
import { useAppDispatch } from "./redux";

type RedeemCouponProps = {
  onSuccess?: (props: { isVoucher: boolean }) => void;
  onError?: () => void;
};

const useRedeemCoupon = ({ onSuccess, onError }: RedeemCouponProps) => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarDescription, setSnackbarDescription] = useState("");
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const [checkCode, { loading }] = useStoreDiscountMutation();
  const [getVirtualWalletBalance] = useGetVirtualWalletBalanceLazyQuery();

  const redeemCoupon = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
    async (code: string) => {
      const parsedCoupon = code?.toUpperCase();

      const { data, errors } = await checkCode({
        variables: { code: parsedCoupon },
      });

      const isCodeCorrect = !!data?.storeDiscount;
      const errorMsg = errors?.[0]?.message as string;
      const isCouponType = data?.storeDiscount.discountType === StoredDiscountType.Coupon;
      const creditAmount = data?.storeDiscount.discountValue;

      const title = isCodeCorrect
        ? isCouponType
          ? t("dialogs.couponActivated")
          : t("dialogs.couponAdded", { amount: priceFormatter(creditAmount) })
        : errorMsg;

      const description = isCodeCorrect
        ? isCouponType
          ? ""
          : t("dialogs.couponCreditsAdded")
        : t("dialogs.couponFindOtherAndTryAgain");

      if (isCouponType) {
        appDispatch(setDraftVoucher(data?.storeDiscount?.discountCode));
      }
      if (isCodeCorrect && !isCouponType) {
        const { data } = await getVirtualWalletBalance();

        if (data) {
          appDispatch(setUserVirtualWalletBalance(data?.getVirtualWalletBalance?.balance));
        }
      }
      setSnackbarText(title);
      setSnackbarDescription(description);

      if (isCodeCorrect) {
        onSuccess?.({ isVoucher: !!isCodeCorrect });
        return code;
      }

      onError?.();
      return false;
    },
    [checkCode, priceFormatter, appDispatch, getVirtualWalletBalance, onSuccess, onError, t],
  );

  return useMemo(
    () => ({
      redeemCoupon,
      loadingCoupon: loading,
      title: snackbarText,
      description: snackbarDescription,
    }),
    [loading, snackbarText, snackbarDescription, redeemCoupon],
  );
};

export default useRedeemCoupon;
